/**
 * useVendorPermissions Hook
 *
 * Centralized permission checks for vendor-related actions.
 * Two levels of control:
 * 1. Vendor-level: When vendor is inactive, most actions are disabled
 * 2. Item-level: When a specific item (contact/location/bank) is inactive, edit/set-default are disabled
 *
 * Designed to be extended for role-based access control (RBAC) in the future.
 *
 * Usage:
 * // Detail page (edit mode)
 * const { canEdit, canAdd, getItemPermissions } = useVendorPermissions(vendor);
 *
 * // Form wizard - pass isCreateMode to enable all actions in create mode
 * const { getItemPermissions } = useVendorPermissions(vendor, { isCreateMode: !isEditMode });
 *
 * <Button disabled={!canEdit}>Edit Vendor</Button>
 * <Button disabled={!getItemPermissions(record).canEdit}>Edit Item</Button>
 */

import {
  getToggleStatusStyle as sharedGetToggleStatusStyle,
  calculateBaseItemPermissions,
  type VendorItemPermissionInput,
  type BaseItemPermissions,
  type ToggleStatusStyleConfig,
} from '@/utils/permissionUtils';
import type { GlobalToken } from 'antd';

// Re-export types for backward compatibility
export type { ToggleStatusStyleConfig };

export interface VendorPermissionInput {
  isActive?: boolean;
}

export interface PermissionOptions {
  /** When true, treats entity as active (for create mode in wizards) */
  isCreateMode?: boolean;
}

export type ItemPermissionInput = VendorItemPermissionInput;

export interface VendorPermissions {
  /** Whether the vendor can be edited (header, financial info, etc.) */
  canEdit: boolean;
  /** Whether new items can be added to the vendor (contacts, locations, banks) */
  canAdd: boolean;
  /** Whether items can be deleted from the vendor */
  canDelete: boolean;
  /** Whether the vendor status can be toggled */
  canToggleStatus: boolean;
  /** Get item-level permissions for a specific record in a table */
  getItemPermissions: (item: ItemPermissionInput | null) => ItemPermissions;
}

export type ItemPermissions = BaseItemPermissions;

/**
 * Get toggle status action style configuration
 * Use this to ensure consistent styling across all toggle status actions
 *
 * @param itemPerms - Item permissions from getItemPermissions()
 * @param token - Ant Design theme token
 * @returns Style configuration for toggle status action
 *
 * @example
 * const itemPerms = getItemPermissions(record);
 * const toggleStyle = getToggleStatusStyle(itemPerms, token);
 * {
 *   key: 'toggleStatus',
 *   label: itemPerms.isItemActive ? t('vendor.actions.deactivate') : t('vendor.actions.activate'),
 *   danger: toggleStyle.danger,
 *   disabled: toggleStyle.disabled,
 *   style: toggleStyle.style,
 * }
 */
export function getToggleStatusStyle(
  itemPerms: ItemPermissions,
  token: GlobalToken
): ToggleStatusStyleConfig {
  return sharedGetToggleStatusStyle(itemPerms, token);
}

/**
 * Hook to check vendor-level permissions and get item-level permission checker
 *
 * @param vendor - Vendor object with at least isActive field, or null
 * @param options - Optional configuration
 * @param options.isCreateMode - When true, treats vendor as active (for wizards in create mode)
 * @returns Permission flags for vendor actions and a function to get item permissions
 *
 * @example
 * // In VendorDetailPage
 * const { canEdit, canAdd, getItemPermissions } = useVendorPermissions(vendor);
 *
 * // In VendorFormWizard (handles both create and edit mode)
 * const { getItemPermissions } = useVendorPermissions(existingVendor, { isCreateMode: !isEditMode });
 *
 * // Disable edit button when vendor is inactive
 * <Button disabled={!canEdit}>{t('vendor.actions.edit')}</Button>
 *
 * // In table action column - disable based on both vendor AND item status
 * {
 *   key: 'edit',
 *   disabled: !getItemPermissions(record).canEdit,
 * }
 */
export function useVendorPermissions(
  vendor: VendorPermissionInput | null | undefined,
  options?: PermissionOptions
): VendorPermissions {
  // In create mode, treat vendor as active since we're creating a new one
  const vendorIsActive = options?.isCreateMode ? true : (vendor?.isActive ?? false);

  // Status-based permissions
  // When vendor is inactive, editing and adding are disabled
  // Future: Add role-based checks here
  // e.g., const { user } = useAuth();
  // const hasEditRole = user?.roles?.includes('vendor:edit');

  /**
   * Get permissions for a specific item (contact, location, or bank account)
   * Combines vendor-level and item-level status checks
   */
  const getItemPermissions = (item: ItemPermissionInput | null): ItemPermissions => {
    return calculateBaseItemPermissions(vendorIsActive, item);
  };

  return {
    canEdit: vendorIsActive,
    canAdd: vendorIsActive,
    canDelete: vendorIsActive,
    canToggleStatus: true, // Always allow toggle (to reactivate vendor)
    getItemPermissions,
  };
}

/**
 * @deprecated Use useVendorPermissions().getItemPermissions() instead
 * Hook to check item-level permissions within a vendor
 * Used for contacts, locations, and bank accounts tables
 */
export function useVendorItemPermissions(
  vendor: VendorPermissionInput | null | undefined,
  item: ItemPermissionInput | null
): ItemPermissions {
  const { getItemPermissions } = useVendorPermissions(vendor);
  return getItemPermissions(item);
}

export default useVendorPermissions;
